import { useState, useEffect } from 'react';
import { Fade, Paper, Stack, Typography, useTheme, Checkbox, IconButton } from '@mui/material';
import { DisplayTable, roomDetailClasses } from '../../../utility';
import APIService from '../../../../api/apiService';
import LoadingSpinner from '../../../utility/LoadingSpinner';
import VisibilityIcon from '@mui/icons-material/Visibility';
import _TransferDetailsPopup from './_TransferDetailsPopup';

const _TransfersTab = props => {
  const { currDarkThemeClass = '', cropplanid } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [transfers, setTransfers] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);

  const theme = useTheme();

  const handleViewDetails = transfer => {
    setSelectedTransfer(transfer);
    setPopupOpen(true);
  };

  const fetchTransfers = async () => {
    setIsLoading(true);
    try {
      const url = cropplanid ? `cropplan-transfers/?cropplan_id=${cropplanid}` : `cropplan-transfers/`;
      const response = await APIService.fetchInstance(url);
      if (response.status === 200) {
        const data = response.data;
        setTransfers(Array.isArray(data) ? data : data?.results || []);
      }
    } catch (error) {
      console.error('Error fetching transfers:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTransfers();
  }, [cropplanid]);

  const handleCheckboxChange = id => {
    if (selectedRows.includes(id)) {
      setSelectedRows(selectedRows.filter(rowId => rowId !== id));
      setSelectAll(false);
    } else {
      setSelectedRows([...selectedRows, id]);
    }
  };

  const handleSelectAllChange = () => {
    if (selectAll) {
      setSelectedRows([]);
    } else {
      setSelectedRows(transfers.map(t => t.id));
    }
    setSelectAll(!selectAll);
  };

  const transferTableTitles = [
    {
      title: (
        <Checkbox
          checked={selectAll}
          onChange={handleSelectAllChange}
          style={{ color: theme?.palette?.grey?.['400'] }}
          indeterminate={selectedRows.length > 0 && selectedRows.length < transfers.length}
        />
      ),
    },
    { title: '#' },
    { title: 'Destination' },
    { title: 'Type' },
    { title: 'Route' },
    { title: 'Invoice No.' },
    { title: 'Est. Dept' },
    { title: 'Est. Arr' },
    { title: 'Transporter' },
    { title: 'Drivers' },
    { title: 'Vehicles' },
    { title: 'Details' },
  ];

  const transferEntries = transfers.map((val, idx) => [
    <Checkbox
      key={`checkbox-${idx}`}
      checked={selectedRows.includes(val.id)}
      onChange={() => handleCheckboxChange(val.id)}
      style={{ color: theme?.palette?.grey?.['400'] }}
    />,
    `${idx + 1}`.padStart(2, '0'),
    val.destination_name || '-',
    val.transfer_type_name || '-',
    val.planned_route || '-',
    val.invoice_number || '-',
    val.est_dept_date_time ? new Date(val.est_dept_date_time).toLocaleString() : '-',
    val.est_arr_date_time ? new Date(val.est_arr_date_time).toLocaleString() : '-',
    val.transporter_name || '-',
    val.drivers_names ? val.drivers_names.join(', ') : '-',
    val.vehicles_names ? val.vehicles_names.join(', ') : '-',
    <IconButton key={`view-${idx}`} onClick={() => handleViewDetails(val)} size="small" style={{ padding: 0 }}>
      <VisibilityIcon sx={{ color: theme?.palette?.grey?.['400'] }} />
    </IconButton>,
  ]);

  const transfersTableValues = {
    entity: 'Transfers',
    tableEntries: transferEntries,
    toShowAddBtn: false,
    tableTitles: transferTableTitles,
    animationClass: 'farmsTabContent',
    currDarkThemeClass,
    isLoading,
  };

  const tabTitleTypoProps = {
    className: `${roomDetailClasses?.tabTitleTypo} ${currDarkThemeClass}`,
  };

  const paperProps = {
    className: `${roomDetailClasses?.detailLightsPaper} ${currDarkThemeClass} roomDetailTabsContent`,
    square: true,
    variant: 'none',
  };

  return (
    <>
      <Fade in={true} timeout={1000}>
        <Paper {...paperProps}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" paddingY={1} width="100%">
            <Typography {...tabTitleTypoProps}>
              {`Transfers (${transfers.length})`}
              {selectedRows.length > 0 && (
                <span style={{ fontSize: '0.8em', opacity: 0.7, marginLeft: '8px' }}>- {selectedRows.length} selected</span>
              )}
            </Typography>
          </Stack>
          {isLoading ? (
            <LoadingSpinner currDarkThemeClass={currDarkThemeClass} message="Loading Transfers Data" />
          ) : (
            <DisplayTable {...transfersTableValues} />
          )}
        </Paper>
      </Fade>
      <_TransferDetailsPopup
        open={popupOpen}
        onClose={() => setPopupOpen(false)}
        transfer={selectedTransfer}
        currDarkThemeClass={currDarkThemeClass}
      />
    </>
  );
};

export default _TransfersTab;
